// root '../../../';

// dependencies
import { Child } from 'pym.js';
import * as d3 from 'd3';
import Zion from './utils/Zion';

// polyfills
// import fetch from 'unfetch';
import './utils/polyfills'; // (includes Array.findIndex && Array.find)

// utils & graphic
import { stylePremium } from './utils/helpers';

// styles
import './styles/graphic.scss';

/* develblock:start */
import meta from './data/meta.json';

const pkg = require('../package.json');

if (process.env.NODE_ENV === 'development') {
  console.clear();
}

d3.timeFormatDefaultLocale({
  periods: ['a.m.', 'p.m.'],
  days: [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
  ],
  shortDays: ['Sun', 'Mon', 'Tues', 'Wed', 'Thurs', 'Fri', 'Sat'],
  months: [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ],
  shortMonths: [
    'Jan.',
    'Feb.',
    'March',
    'April',
    'May',
    'June',
    'July',
    'Aug.',
    'Sept.',
    'Oct.',
    'Nov.',
    'Dec.',
  ],
});

const DATA_URL = 'https://ix.cnn.io/data/mortgage-rates/mortgage_rates_timeseries.json';
const SERIES_KEY = 'rate30';
const SERIES_COLOR = '#7c4ea5';
const REFRESH_MS = 15 * 60 * 1000; // weekly release, but a Thursday update lands without a reload

const pTime = d3.timeParse('%Y-%m-%d');

// Rolling windows off the latest survey date, so the ranges stay true without
// anyone editing a start year. These replace what used to be two graphics.
// The keys stay span-based because the param has to survive the window rolling
// forward; only the reader-facing labels are dated.
const views = [
  { key: '10y', years: 10 },
  { key: '20y', years: 20 },
  { key: '30y', years: 30 },
];

const DEFAULT_VIEW = '10y';

let activeView = DEFAULT_VIEW;
let feed = null;
let series = [];
let viewData = [];
let latest = null;
let resizeTimer = null;

// eslint-disable-next-line no-use-before-define
window.pymChild = new Child({ id: pkg.name });

// d3.format and toFixed both round the stored binary value, so 3.15 formats as
// 3.1. Rounding through two extra places first returns the number an editor
// expects.
function fixed(value, places) {
  const scale = 10 ** places;
  return (Math.round(Math.round(value * scale * 100) / 100) / scale).toFixed(places);
}

// ?view=20y on the embed URL. Digits only, so 20, 20y and 20-year all land.
function requestedView() {
  const match = /[?&]view=([^&#]+)/.exec(window.location.search);
  if (!match) return DEFAULT_VIEW;
  const years = decodeURIComponent(match[1]).replace(/[^0-9]/g, '');
  const view = views.find(v => String(v.years) === years);
  return view ? view.key : DEFAULT_VIEW;
}

// Keep the param in step with the buttons so the URL for any range is copyable.
// URLSearchParams would be tidier but isn't there on the older browsers the rig
// still targets.
function syncUrl(key) {
  if (!window.history || !window.history.replaceState) return;
  const kept = window.location.search.replace(/^\?/, '').split('&')
    .filter(part => part && part.indexOf('view=') !== 0);
  kept.push(`view=${key}`);
  window.history.replaceState(null, '', `${window.location.pathname}?${kept.join('&')}${window.location.hash}`);
}

function pointWord(value) {
  return Math.abs(value) > 1 ? 'percentage points' : 'percentage point';
}

function changePhrase(value, threshold) {
  if (Math.abs(value) < threshold) return null;
  return `${value > 0 ? 'up' : 'down'} ${fixed(Math.abs(value), 2)} ${pointWord(value)}`;
}

function updateDek() {
  const el = document.querySelector('.subhed.dek');
  if (!el || !feed || !latest) return;

  const rate = feed.rates[SERIES_KEY];
  const week = changePhrase(rate.changeWeek, 0.02);
  const year = changePhrase(rate.changeYear, 0.05);

  const key = '<span class="dek-key">30-year fixed mortgage rate</span>';
  const level = `<b>${fixed(latest.value, 2)}%</b>`;

  const first = `The average US ${key} is ${level}, ${week ? `${week} from` : 'little changed from'} a week earlier.`;
  const second = year ? ` It's ${year} from a year ago.` : ' It sits about where it did a year ago.';

  el.innerHTML = first + second;
}

function updateFootnote() {
  const el = document.querySelector('.footnote');
  if (!el || !feed) return;
  el.innerHTML = `Note: Data current through the survey week ending ${feed.surveyDateStr}. Last updated ${feed.lastUpdatedStr}.`;
}

function updateSource() {
  const el = document.querySelector('.source');
  if (!el || !feed || !feed.sourceUrl) return;
  el.innerHTML = `Source: <a href="${feed.sourceUrl}">${feed.source}</a>`;
}

function showMessage(text) {
  const container = d3.select('#graphic');
  container.select('svg').remove();
  container.select('p.chart-message').remove();
  container.append('p')
    .attr('class', 'chart-message')
    .text(text);
  window.pymChild.sendHeight();
}

function draw(animate) {
  const container = d3.select('#graphic');
  container.select('svg').remove();
  container.select('p.chart-message').remove();

  // A resize can fire before the feed lands; the load path owns the empty state.
  if (!latest || !viewData.length) return;

  const initialWidth = container.node().clientWidth;
  const isMobile = initialWidth <= 500;

  // 16:9, floored so a 320px board still has room for five gridlines.
  const height = Math.max(260, Math.min(440, Math.round(initialWidth * 0.5625)));

  const margins = {
    top: 16,
    // Sized to the widest endpoint line, which is the date rather than the
    // value: "March 27" runs 52px at 12px bold, plus the 9px offset.
    right: isMobile ? 64 : 68,
    bottom: 24,
    left: isMobile ? 26 : 30,
  };

  const chartWidth = initialWidth - margins.left - margins.right;
  const chartHeight = height - margins.top - margins.bottom;

  const xScale = d3.scaleTime()
    .domain([viewData[0].date, latest.date])
    .range([0, chartWidth]);

  // Zero baseline: the area fill has to sit on a real floor, not a cropped one.
  const yScale = d3.scaleLinear()
    .domain([0, d3.max(viewData, d => d.value)])
    .nice()
    .range([chartHeight, 0]);

  // Whole percentages only, so '.0f' can never print two ticks with the same label.
  const yTickValues = yScale.ticks(isMobile ? 4 : 5).filter(t => Number.isInteger(t));

  // Keep only whole-year ticks: a sub-year tick would format as a duplicate
  // year label. Every window here is long enough that some always survive.
  // Mobile asks for 4 rather than 3 because 3 pushes the 10-year window onto a
  // five-year step, which labels only 2020 and 2025 across the whole axis.
  const rawXTicks = xScale.ticks(isMobile ? 4 : 5);
  const yearXTicks = rawXTicks.filter(d => +d3.timeYear(d) === +d);
  const xTickValues = yearXTicks.length >= 2 ? yearXTicks : rawXTicks;

  const xAxis = d3.axisBottom(xScale)
    .tickValues(xTickValues)
    .tickSizeOuter(0)
    .tickFormat(d3.timeFormat('%Y'));

  const yAxis = d3.axisLeft(yScale)
    .tickValues(yTickValues)
    .tickSize(-chartWidth)
    .tickPadding(6)
    .tickFormat((d, i) => (i === yTickValues.length - 1 ? `${d3.format('.0f')(d)}%` : d3.format('.0f')(d)));

  const line = d3.line()
    .curve(d3.curveMonotoneX)
    .x(d => xScale(d.date))
    .y(d => yScale(d.value));

  const area = d3.area()
    .curve(d3.curveMonotoneX)
    .x(d => xScale(d.date))
    .y0(yScale(0))
    .y1(d => yScale(d.value));

  const svg = container.append('svg')
    .attr('width', initialWidth)
    .attr('height', height)
    .attr('class', animate ? 'chart-fade' : null)
    .attr('role', 'img')
    // No "past N years" here either: a screen reader hits the same collision
    // with the loan term that the button labels do.
    .attr('aria-label', `Average US 30-year fixed mortgage rate, ${d3.timeFormat('%B %Y')(viewData[0].date)} to present. Latest reading ${fixed(latest.value, 2)} percent.`);

  const g = svg.append('g')
    .attr('transform', `translate(${[margins.left, margins.top]})`);

  g.append('g')
    .attr('class', 'grid x')
    .attr('transform', `translate(${[0, chartHeight]})`)
    .call(xAxis);

  g.append('g')
    .attr('class', 'grid y')
    .call(yAxis);

  g.select('.grid.y .domain').remove();

  // Zero is a value the reader measures against, so it gets its own darker rule
  // instead of the gridline the axis would draw.
  g.selectAll('.grid.y .tick')
    .filter(d => d === 0)
    .select('line')
    .attr('class', 'zero-line');

  g.append('path')
    .attr('class', 'area')
    .datum(viewData)
    .attr('fill', SERIES_COLOR)
    .attr('d', area);

  g.append('path')
    .attr('class', 'series-line')
    .datum(viewData)
    .attr('stroke', SERIES_COLOR)
    .attr('stroke-width', isMobile ? 2 : 2.25)
    .attr('d', line);

  const endpoint = g.append('g')
    .attr('class', 'endpoint')
    .attr('transform', `translate(${[xScale(latest.date), yScale(latest.value)]})`);

  // Filled disc at the dot's own radius, behind the dot: the one animated
  // element on the chart, marking the value as live rather than as-of-publication.
  endpoint.append('circle')
    .attr('class', 'pulse')
    .attr('r', 4)
    .attr('fill', SERIES_COLOR);

  endpoint.append('circle')
    .attr('r', 4)
    .attr('fill', SERIES_COLOR)
    .attr('stroke', '#fff')
    .attr('stroke-width', 1.5);

  // Two lines centered on the point: the date carries the weight, the value
  // reads underneath it.
  endpoint.append('text')
    .attr('class', 'endpoint-date')
    .attr('x', 9)
    .attr('dy', '-0.25em')
    .text(d3.timeFormat('%b %-d')(latest.date));

  endpoint.append('text')
    .attr('class', 'endpoint-value')
    .attr('x', 9)
    .attr('dy', '0.95em')
    .text(`${fixed(latest.value, 2)}%`);

  window.pymChild.sendHeight();
}

// Measured back from the latest survey week rather than today, so the window is
// tied to the data and not to when the page happens to load.
function windowStart(view) {
  return d3.timeYear.offset(latest.date, -view.years);
}

// Labeled by where the line starts rather than by the span it covers. A button
// reading "30 years" sits inches from "30-year fixed mortgage rate" and reads as
// the loan term, which is the one thing this control does not change.
function viewLabel(view) {
  const first = series.find(d => d.date >= windowStart(view));
  return `Since ${(first ? first.date : windowStart(view)).getFullYear()}`;
}

function setView(key, animate) {
  activeView = key;
  viewData = series.filter(d => d.date >= windowStart(views.find(v => v.key === key)));
  draw(animate);
}

function createControls() {
  const container = d3.select('#graphic');
  if (!container.select('.controls').empty()) return;

  const controls = container.insert('div', ':first-child')
    .attr('class', 'controls')
    .attr('role', 'group')
    .attr('aria-label', 'Choose a time range');

  controls.selectAll('button')
    .data(views)
    .enter()
    .append('button')
    .attr('type', 'button')
    .attr('class', d => (d.key === activeView ? 'active' : null))
    .attr('aria-pressed', d => d.key === activeView)
    .text(d => viewLabel(d))
    .on('click', (d) => {
      if (d.key === activeView) return;
      setView(d.key, true);
      syncUrl(activeView);
      controls.selectAll('button')
        .classed('active', b => b.key === activeView)
        .attr('aria-pressed', b => b.key === activeView);
    });
}

function parseFeed(json) {
  feed = json;
  series = json.data
    .filter(d => d[SERIES_KEY] != null)
    .map(d => ({ date: pTime(d.date), value: +d[SERIES_KEY] }));
  latest = series[series.length - 1];
}

function loadData(isRefresh) {
  return d3.json(DATA_URL)
    .then((json) => {
      if (!json || !json.data || !json.data.length) throw new Error('Empty feed');

      parseFeed(json);
      updateDek();
      updateFootnote();
      updateSource();

      if (!isRefresh) createControls();
      setView(activeView, false);
    })
    .catch((error) => {
      console.error('Failed to load mortgage rate data:', error);
      if (!isRefresh) showMessage('Data is currently unavailable.');
    });
}

window.onload = () => {
  if (process.env.NODE_ENV === 'development') {
    Object.keys(meta).forEach((id) => {
      const node = document.querySelector(`.${id}`);
      if (node) node.innerHTML = meta[id];
    });
  }

  window.pymChild.sendHeight();

  if (window.pymChild.parentUrl) {
    // Zion registers the page view on construction.
    new Zion(pkg.name, window.pymChild.parentUrl.split('?')[0]); // eslint-disable-line no-new
  }

  stylePremium(false, false);

  activeView = requestedView();

  loadData(false);
  setInterval(() => loadData(true), REFRESH_MS);

  window.onresize = () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => draw(false), 150);
  };
};
